/**
 * One to five stars — the learner's own verdict on a lesson.
 *
 * The only number in the app that is not derived from anything. Every other
 * figure on the review screen is computed from `attempts`; this one is an
 * opinion, and the code must never quietly improve it.
 *
 * THE ENDS ARE LABELLED, and that is not decoration. An unlabelled 1-to-5
 * means something different in week one than in week six — the same rule that
 * felt like a 2 in September gets a 2 again in October for a completely
 * different reason, and the comparison the whole feature exists for quietly
 * stops meaning anything. Naming the ends pins the scale to something outside
 * the learner's mood.
 *
 * SKIPPING IS FREE. There is no confirm, no "are you sure", and no consequence
 * to walking past it. A rating that has to be given to get to the next screen is
 * a rating that gets tapped at random within a week, and random taps are worse
 * than no data: they look like data.
 */
import { Star } from 'lucide-react';

import { cn } from '@/lib/utils';

import { STAR_LABELS } from './score';

type Props = {
  /** The current rating, or null when this lesson has never been rated. */
  value: number | null;
  onRate: (stars: number) => void;
  disabled?: boolean;
  size?: 'sm' | 'lg';
  /** Names the lesson in the accessible label, so the control says what it rates. */
  label?: string;
};

export function StarRating({ value, onRate, disabled, size = 'sm', label }: Props) {
  const starSize = size === 'lg' ? 'size-9' : 'size-4';

  return (
    <div
      className={cn('flex items-center', size === 'lg' ? 'gap-1.5' : 'gap-0.5')}
      role="radiogroup"
      aria-label={label ? `How well did ${label} land?` : 'How well did it land?'}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = value !== null && star <= value;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} — ${STAR_LABELS[star]}`}
            disabled={disabled}
            onClick={() => onRate(star)}
            className={cn(
              'focus-visible:ring-ring rounded transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50',
              size === 'lg' ? 'p-1.5' : 'p-0.5',
              filled ? 'text-streak' : 'text-muted-foreground/40 hover:text-muted-foreground',
            )}
          >
            <Star className={starSize} fill={filled ? 'currentColor' : 'none'} strokeWidth={1.8} />
          </button>
        );
      })}
    </div>
  );
}
